import React from "react";

const reEsc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
export const countIn = (text, q) => {
  if (!q || q.length < 2 || !text) return 0;
  try { return (String(text).match(new RegExp(reEsc(q), "gi")) || []).length; } catch { return 0; }
};

// Text with <mark> around every match of q (q < 2 chars = plain text).
export function Hi({ text, q }) {
  const s = String(text ?? "");
  if (!q || q.length < 2) return s;
  const parts = s.split(new RegExp(`(${reEsc(q)})`, "gi"));
  return parts.map((p, i) => (i % 2 ? <mark key={i}>{p}</mark> : <React.Fragment key={i}>{p}</React.Fragment>));
}

export const Seg = ({ value, options, onChange, testid, disabled }) => (
  <span className="cfv-en-seg" data-testid={testid}>
    {options.map(([v, l]) => (
      <button key={v} type="button" disabled={disabled} className={value === v ? "on" : ""} aria-pressed={value === v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}>{l}</button>
    ))}
  </span>
);

const HEAD_RE = /^(chief complaint|subjective|objective|assessment|plan|history( of present illness)?|hpi|exam(ination)?|physical exam|review of systems|ros|medications?|allergies|impression|diagnos[ie]s|vitals?|labs?|follow[- ]?up|social history|family history|past medical history|instructions?)\s*:?\s*$/i;

// Note body: short section titles → headings, "- " → bullets, else paragraphs.
// "Title: text" on one line → heading + paragraph.
export function NoteBody({ text, q }) {
  const out = [];
  let list = null;
  const flush = () => { if (list) { out.push(<ul key={`u${out.length}`}>{list}</ul>); list = null; } };
  String(text || "").split(/\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    if (/^[-•]\s+/.test(line)) { (list = list || []).push(<li key={i}><Hi text={line.replace(/^[-•]\s+/, "")} q={q} /></li>); return; }
    flush();
    const m = /^([A-Za-z][A-Za-z /-]{1,40}):\s*(.*)$/.exec(line);
    if (HEAD_RE.test(line)) { out.push(<h5 key={i}><Hi text={line.replace(/:\s*$/, "")} q={q} /></h5>); return; }
    if (m && HEAD_RE.test(m[1])) {
      out.push(<h5 key={`h${i}`}><Hi text={m[1]} q={q} /></h5>);
      if (m[2]) out.push(<p key={i}><Hi text={m[2]} q={q} /></p>);
      return;
    }
    out.push(<p key={i}><Hi text={line} q={q} /></p>);
  });
  flush();
  return <div className="cfv-en-note" data-testid="cfv-en-note-body">{out}</div>;
}

const ns = (v) => (v == null || v === "" ? <i className="ns">not stated</i> : <b>{v}</b>);

export const rxText = (rx) => rx.map((m) => [m.drug_name || m.name, m.strength, m.form, m.sig, m.rxnorm_code, m.ndc_number].filter(Boolean).join(" ")).join("\n");

export function RxTab({ rx, rp, q }) {
  return (
    <div data-testid="cfv-en-tab-rx-panel">
      {rx.map((m, i) => (
        <div className="cfv-en-rx" key={m.id || i} data-testid={`cfv-en-rx-${i}`}>
          <div><b><Hi text={m.drug_name || m.name || "—"} q={q} /></b>{m.strength || m.form ? <span className="sub"> <Hi text={[m.strength, m.form].filter(Boolean).join(" ")} q={q} /></span> : null}</div>
          {m.sig ? <div><Hi text={m.sig} q={q} /></div> : null}
          <div className="kv">Days supply {ns(m.days_supply)} · Refills {ns(m.refills)} · Total qty {ns(m.total_qty)}</div>
          {m.rxnorm_code || m.ndc_number ? <div className="mono">{[m.rxnorm_code && `RxNorm ${m.rxnorm_code}`, m.ndc_number && `NDC ${m.ndc_number}`].filter(Boolean).join(" · ")}</div> : null}
        </div>
      ))}
      {rp.length ? (
        <div className="cfv-en-block" data-testid="cfv-en-rp">
          <div className="cfv-en-h">Needs re-prescribing · {rp.length}</div>
          {rp.map((m, i) => (
            <div className="cfv-en-rx" key={m.id || i}>
              <div><b>{m.drug_name || m.name || m.label || "—"}</b>{m.strength ? <span className="sub"> {m.strength}</span> : null}</div>
              {(m.reason || m.sig || m.note) ? <div><Hi text={m.reason || m.sig || m.note} q={q} /></div> : null}
            </div>
          ))}
          <div className="cfv-en-grey">Flagged by the claim engine. Prescriptions are not sent from here.</div>
        </div>
      ) : null}
    </div>
  );
}

export const piText = (sections, guidance, lang) => [
  ...guidance.map((g) => `${g.drug} ${lang === "es" ? (g.body_es || g.body_en) : (g.body_en || g.body_es)}`),
  ...sections.map((s) => `${lang === "es" ? (s.title_es || s.title_en) : (s.title_en || s.title_es)} ${lang === "es" ? (s.body_es || s.body_en) : (s.body_en || s.body_es)}`),
].join("\n");

export function InstructionsTab({ sections, guidance, lang, setLang, q }) {
  const L = (en, es) => (lang === "es" ? (es || en) : (en || es));
  return (
    <div data-testid="cfv-en-tab-pi-panel">
      <div className="cfv-en-row"><span className="cfv-en-grey">Care plan for the patient · {sections.length} section{sections.length === 1 ? "" : "s"}</span><Seg value={lang} onChange={setLang} options={[["en", "EN"], ["es", "ES"]]} testid="cfv-en-pi-lang" /></div>
      {guidance.length ? (
        <div className="cfv-en-block" data-testid="cfv-en-newmed">
          <div className="cfv-en-h">New medication</div>
          {guidance.map((g, i) => <p key={i}><b><Hi text={g.drug} q={q} /></b> — <Hi text={L(g.body_en, g.body_es)} q={q} /></p>)}
        </div>
      ) : null}
      {sections.map((s, i) => (
        <div key={s.key || i} className="cfv-en-sect">
          <div className="cfv-en-h"><Hi text={L(s.title_en, s.title_es)} q={q} /></div>
          <p><Hi text={L(s.body_en, s.body_es)} q={q} /></p>
        </div>
      ))}
    </div>
  );
}

const CAT = { follow_up: "Follow-up", followup: "Follow-up" };
const catLabel = (c) => { const k = String(c || "").toLowerCase().replace(/[\s-]+/g, "_"); if (!k) return ""; if (CAT[k]) return CAT[k]; const t = k.replace(/_/g, " "); return t.charAt(0).toUpperCase() + t.slice(1); };
const shortDate = (d) => { if (!d) return "—"; const t = new Date(`${String(d).slice(0, 10)}T12:00:00`); return isNaN(t) ? String(d) : t.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };

export const msgText = (messages, lang) => messages.map((m) => `${m.label || ""} ${catLabel(m.category)} ${lang === "es" ? (m.text_es || m.text_en) : (m.text_en || m.text_es)}`).join("\n");

export function MessagesTab({ messages, anchor, lang, setLang, q }) {
  const first = anchor || messages[0]?.send_at;
  return (
    <div data-testid="cfv-en-tab-msg-panel">
      <div className="cfv-en-row"><span className="cfv-en-grey">By SMS · {messages.length} planned{first ? ` from ${shortDate(first)}` : ""}</span><Seg value={lang} onChange={setLang} options={[["en", "EN"], ["es", "ES"]]} testid="cfv-en-msg-lang" /></div>
      {messages.map((m, i) => {
        const cat = catLabel(m.category);
        const showCat = cat && cat.toLowerCase() !== String(m.label || "").toLowerCase();
        return (
          <div className="cfv-en-2c" key={m.id || i} data-testid={`cfv-en-msg-${i}`}>
            <div className="l"><b>{shortDate(m.send_at)}</b>{m.offset_days != null ? <span>Day {m.offset_days}</span> : null}</div>
            <div>
              {m.label ? <div className="t"><Hi text={m.label} q={q} /></div> : null}
              {showCat ? <div className="cfv-en-grey sm"><Hi text={cat} q={q} /></div> : null}
              <div><Hi text={lang === "es" ? (m.text_es || m.text_en) : (m.text_en || m.text_es)} q={q} /></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// "Speaker 0: text" / "Doctor [00:12]: text" → { who, ts, text }.
export const parseTx = (lines) => lines.map((raw) => {
  const m = /^\s*([A-Za-z][A-Za-z0-9 ]{0,20}?)\s*(?:\[(\d{1,2}:\d{2}(?::\d{2})?)\])?\s*:\s*(.*)$/.exec(raw);
  return m ? { who: m[1], ts: m[2] || "", text: m[3] } : { who: "", ts: "", text: raw };
});

export function TranscriptTab({ rows, q }) {
  return (
    <div data-testid="cfv-en-tab-tx-panel">
      <div className="cfv-en-row"><span className="cfv-en-grey">{rows.length} line{rows.length === 1 ? "" : "s"} · used to code this claim</span></div>
      {rows.map((r, i) => (
        <div className="cfv-en-2c" key={i}>
          <div className="l"><b>{r.who || "—"}</b>{r.ts ? <span>{r.ts}</span> : null}</div>
          <div><Hi text={r.text} q={q} /></div>
        </div>
      ))}
    </div>
  );
}
